import React, { useState, useEffect, useRef } from "react";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { Plus, X, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { UserData } from "@/components/LoginPage";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { PvNoAsignadoGate } from "@/components/PvNoAsignadoGate";
import { sessionIsElevated, canCreateProveedorFromPersonalizado } from "@/lib/modulePermissions";
import { bloqueaPersonalizadosExistentes } from "@/lib/sessionUser";
import {
  assignedPuntosVentaFromUser,
  pickDefaultPuntoVenta,
  isPvNoAsignadoApiError,
  PV_NO_ASIGNADO_MSG,
  vendedorBlockedWithoutPv,
} from "@/lib/pvAsignado";
import { refreshOperatorForSale } from "@/lib/sessionUser";
import { authAPI, clientesAPI, productosAPI, opcionesAPI, ventasAPI, type Cliente, type Producto as ProductoAPI, isForbiddenError, isServerError } from "@/lib/api";
import { openPdfTab } from "@/lib/openPdfTab";
import { cn, clampProductSearchHighlight, filterProductSearchMatches, handleProductSearchHotkey, toNum } from "@/lib/utils";
import { productoEsPersonalizado } from "@/lib/ventaStockDisponible";
import { lineImportePeso, roundToPeso } from "@/lib/roundToPeso";
import {
  ConfiguradorProductoDialog,
  type PersonalizadoQuoteSnapshot,
} from "@/modules/Stock/ConfiguradorProductoDialog";
import { ClientesABM } from "@/modules/Clientes/ClientesABM";
import { ComboProductosDialog } from "./ComboProductosDialog";
import {
  findRegularLine,
  groupComboVisualRows,
  comboVisualCodigo,
  comboVisualNombre,
  comboVisualPrecioSum,
  lineKeyOf,
  regularLineKey,
  removeLinesByKey,
  syncComboCantidad,
  syncComboPatch,
  type ComboCapableLine,
} from "@/lib/comboLine";

interface Producto {
  id?: number | null;
  codigo: string;
  nombre: string;
  precioBase: number;
  cantidad: number;
  descuento1: number;
  descuento2: number;
  /** Línea personalizada diferida (sin Producto hasta la venta). */
  configSnapshot?: {
    selecciones: Record<string, unknown>;
    selecciones_unificar?: Record<string, unknown> | Record<string, unknown>[];
    proveedor_id?: number | null;
  } | null;
  lineKey?: string;
  comboGroup?: string | null;
  ivaPorcentaje?: number | null;
}

interface PresupuestosFormProps {
  onPresupuestoCreated?: (presupuesto: any) => void;
  user: UserData;
  isActive?: boolean;
}

export function PresupuestosForm({ onPresupuestoCreated, user, isActive = true }: PresupuestosFormProps) {
  const ocultaPersonalizadosExistentes = bloqueaPersonalizadosExistentes(user);
  const [clienteQuery, setClienteQuery] = useState("");
  const [showClienteSuggestions, setShowClienteSuggestions] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [selectedPuntoVenta, setSelectedPuntoVenta] = useState("");
  const [productos, setProductos] = useState<Producto[]>([]);
  const [productoQuery, setProductoQuery] = useState("");
  const [showProductoSuggestions, setShowProductoSuggestions] = useState(false);
  const [productoHighlight, setProductoHighlight] = useState(0);
  const [cantidad, setCantidad] = useState(1);
  const [isConsumidorFinal, setIsConsumidorFinal] = useState(false);
  const [clientesFound, setClientesFound] = useState<Cliente[]>([]);
  const [clienteSearchError, setClienteSearchError] = useState<string | null>(null);
  const [puntosVenta, setPuntosVenta] = useState<any[]>([]);
  const [showNuevoCliente, setShowNuevoCliente] = useState(false);
  const [pvDialogOpen, setPvDialogOpen] = useState(false);
  const [operatorUser, setOperatorUser] = useState<UserData>(user);
  const pvBlocked = vendedorBlockedWithoutPv(operatorUser);
  const canPickPv = sessionIsElevated(operatorUser);

  const productoDropdownRef = useRef<HTMLDivElement>(null);
  const [foundProductos, setFoundProductos] = useState<ProductoAPI[]>([]);
  const [_loadingProducts, setLoadingProducts] = useState(false);
  const [showConfigurador, setShowConfigurador] = useState(false);
  const [showPersonalizadoPicker, setShowPersonalizadoPicker] = useState(false);
  const [personalizadoQuery, setPersonalizadoQuery] = useState("");
  const [foundPersonalizados, setFoundPersonalizados] = useState<ProductoAPI[]>([]);
  const [searchingPersonalizados, setSearchingPersonalizados] = useState(false);
  const [showComboDialog, setShowComboDialog] = useState(false);
  const deferredSeq = useRef(0);

  // Load Puntos de Venta (Predeterminado fresco de /me/; admin sin PV: catálogo)
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    const loadPuntosVenta = async () => {
      try {
        const result = await refreshOperatorForSale(
          user,
          () => authAPI.getCurrentUser(),
          async () =>
            assignedPuntosVentaFromUser({
              puntosVenta: (await opcionesAPI.listPuntosVenta()) || [],
            }),
        );
        if (cancelled) return;
        if (result.user) setOperatorUser(result.user as UserData);
        setPuntosVenta(
          result.list.map((pv) => ({
            id: pv.id,
            codigo: pv.codigo,
            nombre: pv.nombre,
            sucursal: pv.sucursal_nombre || "",
          })),
        );
        setSelectedPuntoVenta(result.selected?.codigo || "");
      } catch (error) {
        console.error("Error loading Puntos de Venta:", error);
        toast.error("Error al cargar puntos de venta");
      }
    };

    loadPuntosVenta();
    return () => {
      cancelled = true;
    };
  }, [user, isActive]);

  // Clear cliente when consumidor final is selected
  useEffect(() => {
    if (isConsumidorFinal) {
      setSelectedCliente(null);
      setClienteQuery("");
    }
  }, [isConsumidorFinal]);

  // Handle click outside to close product dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productoDropdownRef.current && !productoDropdownRef.current.contains(event.target as Node)) {
        setShowProductoSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search clients via API with debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      // Don't search if Consumidor Final or empty
      if (clienteQuery.trim().length > 0 && !isConsumidorFinal) {
        try {
          setClienteSearchError(null);
          const results = await clientesAPI.lookup({ search: clienteQuery, page_size: 10 });
          // Exclude canonical Consumidor Final (codigo=CF) from autocomplete
          setClientesFound((results || []).filter((c) => c.codigo !== "CF"));
        } catch (error) {
          console.error("Búsqueda contextual de cliente:", error);
          setClientesFound([]);
          if (isForbiddenError(error)) {
            setClienteSearchError("Sin permiso para buscar clientes.");
          } else if (isServerError(error)) {
            setClienteSearchError("Error del servidor al buscar clientes.");
          } else {
            setClienteSearchError("No se pudieron buscar clientes.");
          }
        }
      } else {
        setClientesFound([]);
        setClienteSearchError(null);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [clienteQuery, isConsumidorFinal]);

  const filteredClientes = clientesFound;

  // Search products via API with debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (productoQuery.trim().length > 0) {
        setLoadingProducts(true);
        try {
          const results = await productosAPI.list({
            search: productoQuery,
            ...(ocultaPersonalizadosExistentes ? { esPersonalizado: false } : {}),
          });
          const rows = results || [];
          setFoundProductos(
            ocultaPersonalizadosExistentes
              ? rows.filter((p) => !productoEsPersonalizado(p))
              : rows,
          );
        } catch (error) {
          console.error("Error searching products:", error);
          setFoundProductos([]);
        } finally {
          setLoadingProducts(false);
        }
      } else {
        setFoundProductos([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [productoQuery, ocultaPersonalizadosExistentes]);

  useEffect(() => {
    if (!showPersonalizadoPicker) return;
    const search = async () => {
      setSearchingPersonalizados(true);
      try {
        const res = await productosAPI.list({
          search: personalizadoQuery.trim() || undefined,
          esPersonalizado: true,
        });
        const onlyPersonalizados = (res || []).filter(
          (p) =>
            p.esPersonalizado === true ||
            (p as { es_personalizado?: boolean }).es_personalizado === true,
        );
        setFoundPersonalizados(onlyPersonalizados);
      } catch (e) {
        console.error(e);
        setFoundPersonalizados([]);
      } finally {
        setSearchingPersonalizados(false);
      }
    };
    const t = setTimeout(search, personalizadoQuery.trim() ? 300 : 0);
    return () => clearTimeout(t);
  }, [personalizadoQuery, showPersonalizadoPicker]);

  const filteredProductos = filterProductSearchMatches(foundProductos, productoQuery);

  const selectCliente = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setClienteQuery(cliente.nombre);
    setShowClienteSuggestions(false);
    setIsConsumidorFinal(false);
  };

  const addProducto = (producto: ProductoAPI) => {
    const existingProducto = findRegularLine(productos, producto);
    const precio = roundToPeso(producto.precioFinal || producto.precio_final || producto.precioBase);

    if (existingProducto) {
      const existingKey = lineKeyOf(existingProducto);
      setProductos(productos.map(p =>
        lineKeyOf(p) === existingKey
          ? { ...p, cantidad: p.cantidad + cantidad }
          : p
      ));
    } else {
      setProductos([...productos, {
        id: producto.id,
        codigo: producto.codigo,
        nombre: producto.nombre,
        precioBase: precio,
        cantidad: cantidad,
        descuento1: 0,
        descuento2: 0,
        lineKey: producto.id ? regularLineKey(producto.id) : producto.codigo,
      }]);
    }
    setProductoQuery("");
    setCantidad(1);
    setShowProductoSuggestions(false);
    toast.success(`${producto.nombre} agregado`);
  };

  const addComboLines = (lines: ComboCapableLine[]) => {
    setProductos((prev) => [
      ...prev,
      ...lines.map((l) => ({
        id: l.id,
        codigo: l.codigo,
        nombre: l.nombre,
        precioBase: roundToPeso(l.precioBase),
        cantidad: l.cantidad,
        descuento1: l.descuento1,
        descuento2: l.descuento2,
        lineKey: l.lineKey,
        comboGroup: l.comboGroup,
        ivaPorcentaje: l.ivaPorcentaje ?? 21,
      })),
    ]);
    setCantidad(1);
    toast.success(`${lines[0]?.nombre || "A"} + ${lines[1]?.nombre || "B"}`);
  };

  const removeProducto = (key: string) => {
    setProductos(removeLinesByKey(productos, key));
  };

  const updateCantidad = (key: string, newCantidad: number) => {
    if (newCantidad <= 0) return;
    setProductos(syncComboCantidad(productos, key, newCantidad));
  };

  const updateDescuento1 = (key: string, descuento: number) => {
    setProductos(syncComboPatch(productos, key, { descuento1: descuento }));
  };

  const updateDescuento2 = (key: string, descuento: number) => {
    setProductos(syncComboPatch(productos, key, { descuento2: descuento }));
  };

  const addPersonalizadoSnapshot = (snap: PersonalizadoQuoteSnapshot) => {
    deferredSeq.current += 1;
    const lineKey = `deferred-${deferredSeq.current}-${Date.now()}`;
    setProductos((prev) => [
      ...prev,
      {
        id: null,
        codigo: snap.codigoProvisional,
        nombre: snap.nombre,
        precioBase: roundToPeso(snap.precio),
        cantidad: Math.max(1, cantidad),
        descuento1: 0,
        descuento2: 0,
        lineKey,
        configSnapshot: {
          selecciones: snap.selecciones,
          proveedor_id: snap.proveedorId ?? null,
          ...(snap.seleccionesUnificar
            ? { selecciones_unificar: snap.seleccionesUnificar }
            : {}),
        },
      },
    ]);
    setCantidad(1);
  };

  const calcularTotalProducto = (producto: Producto) => {
    return lineImportePeso({
      precio: producto.precioBase,
      cantidad: producto.cantidad,
      descuento1: producto.descuento1,
      descuento2: producto.descuento2,
    });
  };

  const calcularTotal = () => {
    return roundToPeso(productos.reduce((total, p) => total + calcularTotalProducto(p), 0));
  };

  const generateShortNumber = () => {
    return Math.floor(Math.random() * 10000000000).toString().padStart(10, '0');
  };

  const generarPresupuesto = async () => {
    if (vendedorBlockedWithoutPv(operatorUser)) {
      setPvDialogOpen(true);
      return;
    }
    if (!selectedCliente && !isConsumidorFinal) {
      toast.error("Debe seleccionar un cliente o activar Consumidor Final");
      return;
    }
    if (!selectedPuntoVenta) {
      toast.error("Debe seleccionar un punto de venta");
      return;
    }
    if (productos.length === 0) {
      toast.error("Debe agregar al menos un producto");
      return;
    }

    // Exact lookup by canonical codigo=CF (no fuzzy search)
    let clienteForPresupuesto = selectedCliente;
    if (isConsumidorFinal) {
      try {
        const rows = await clientesAPI.lookup({ codigo: "CF" });
        const cf = rows.find((c) => c.codigo === "CF") || rows[0];
        if (cf?.id) {
          clienteForPresupuesto = cf;
        } else {
          toast.error("Cliente Consumidor Final (CF) no encontrado en el sistema");
          return;
        }
      } catch (error) {
        console.error("Error fetching CF client:", error);
        if (isForbiddenError(error)) {
          toast.error("Sin permiso para buscar el cliente Consumidor Final.");
        } else if (isServerError(error)) {
          toast.error("Error del servidor al buscar Consumidor Final.");
        } else {
          toast.error("Cliente Consumidor Final (CF) no encontrado en el sistema");
        }
        return;
      }
    }

    if (!clienteForPresupuesto) {
      toast.error("Error: Cliente no encontrado");
      return;
    }

    const selectedPVObj = puntosVenta.find(pv => pv.codigo === selectedPuntoVenta);
    if (!selectedPVObj) {
      toast.error("Punto de venta inválido");
      return;
    }

    const totalCalculado = calcularTotal();
    const presupuestoData = {
      numero: `PR-${generateShortNumber()}`,
      cliente: clienteForPresupuesto.id,
      punto_venta: selectedPVObj.id,
      fecha: (() => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })(),
      subtotal: roundToPeso(totalCalculado),
      descuento: 0,
      iva: 0,
      total: roundToPeso(totalCalculado),
      estado: 'pendiente',
      items: productos.map(p => {
        const base = {
          codigo_producto: p.codigo,
          nombre_producto: p.nombre,
          cantidad: p.cantidad,
          precio_unitario: roundToPeso(Number(p.precioBase)),
          descuento1: Number(Number(p.descuento1).toFixed(2)),
          descuento2: Number(Number(p.descuento2).toFixed(2)),
          ...(p.comboGroup ? { combo_group: p.comboGroup } : {}),
          ...(p.ivaPorcentaje != null ? { iva_porcentaje: p.ivaPorcentaje } : {}),
        };
        if (p.configSnapshot) {
          return {
            ...base,
            producto: null,
            config_snapshot: p.configSnapshot,
          };
        }
        return {
          ...base,
          producto: p.id ?? null,
        };
      }),
    };

    const generarOperation = async () => {
      try {
        const res = await ventasAPI.createPresupuesto(presupuestoData as any);
        if (onPresupuestoCreated) {
          onPresupuestoCreated(res);
        }

        if (res.id) {
          try {
            const blob = await ventasAPI.downloadPresupuestoPDF(res.id);
            if (blob) {
              await openPdfTab(blob, undefined, { path: `/api/presupuestos/${res.id}/pdf/` });
            }
          } catch (pdfErr) {
            console.error("Error downloading presupuesto PDF:", pdfErr);
          }
        }

        // Reset form
        setSelectedCliente(null);
        setClienteQuery("");
        setProductos([]);
        setIsConsumidorFinal(false);
        setClientesFound([]);

        // Re-select punto de venta (Predeterminado)
        if (puntosVenta.length > 0) {
          const picked = pickDefaultPuntoVenta(
            operatorUser,
            assignedPuntosVentaFromUser(operatorUser),
          );
          setSelectedPuntoVenta(picked?.codigo || puntosVenta[0].codigo);
        }
        return "Presupuesto creado exitosamente";
      } catch (e) {
        console.error(e);
        if (isPvNoAsignadoApiError(e)) {
          setPvDialogOpen(true);
          throw new Error(PV_NO_ASIGNADO_MSG);
        }
        throw e;
      }
    };

    toast.promise(generarOperation(), {
      loading: "Creando presupuesto...",
      success: (msg) => msg,
      error: (e: unknown) => {
        const msg = e instanceof Error ? e.message : "";
        return isPvNoAsignadoApiError(e) || msg === PV_NO_ASIGNADO_MSG
          ? PV_NO_ASIGNADO_MSG
          : "Error al crear presupuesto";
      },
    });
  };

  return (
    <div className="space-y-6">
      <PvNoAsignadoGate
        blocked={pvBlocked}
        dialogOpen={pvDialogOpen}
        onDialogOpenChange={setPvDialogOpen}
      />
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Información de Venta</h3>

        <div className="flex items-center space-x-2 mb-4">
          <Checkbox
            id="consumidorFinal"
            checked={isConsumidorFinal}
            onCheckedChange={(checked) => setIsConsumidorFinal(checked as boolean)}
          />
          <Label
            htmlFor="consumidorFinal"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Consumidor Final (sin datos de cliente)
          </Label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <label className="block text-sm font-medium mb-1">Buscar Cliente</label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <SearchInput
                  value={clienteQuery}
                  onChange={(e) => {
                    setClienteQuery(e.target.value);
                    setShowClienteSuggestions(true);
                  }}
                  onFocus={() => setShowClienteSuggestions(true)}
                  placeholder="Buscar cliente por nombre o código..."
                  disabled={isConsumidorFinal}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-10 whitespace-nowrap"
                disabled={isConsumidorFinal}
                onClick={() => setShowNuevoCliente(true)}
              >
                + Cliente
              </Button>
            </div>
            {showClienteSuggestions && !isConsumidorFinal && (filteredClientes.length > 0 || clienteSearchError || clienteQuery.trim()) && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-input rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {clienteSearchError ? (
                  <div className="px-4 py-2 text-sm text-muted-foreground">{clienteSearchError}</div>
                ) : filteredClientes.length > 0 ? (
                  filteredClientes.map((cliente) => (
                  <button
                    key={cliente.codigo}
                    type="button"
                    onClick={() => selectCliente(cliente)}
                    className="w-full px-4 py-2 text-left hover:bg-muted text-sm flex justify-between items-center"
                  >
                    <span>{cliente.nombre}</span>
                    <span className="text-xs text-muted-foreground">{cliente.codigo}</span>
                  </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No se encontraron clientes</div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Punto de Venta</label>
            {canPickPv ? (
              <select
                className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                value={selectedPuntoVenta}
                onChange={(e) => setSelectedPuntoVenta(e.target.value)}
              >
                {puntosVenta.map((pv) => (
                  <option key={pv.id ?? pv.codigo} value={pv.codigo}>
                    {pv.codigo} · {pv.nombre}
                  </option>
                ))}
              </select>
            ) : (
              <div className="w-full px-4 py-2 border border-input bg-muted rounded-lg text-sm text-muted-foreground">
                {selectedPuntoVenta
                  ? puntosVenta.find(pv => pv.codigo === selectedPuntoVenta)?.nombre + " - " + puntosVenta.find(pv => pv.codigo === selectedPuntoVenta)?.sucursal
                  : "No asignado (o cargando...)"}
              </div>
            )}
          </div>
        </div>

        {(selectedCliente || isConsumidorFinal) && (
          <div className="p-4 border border-input rounded-lg bg-muted/50">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Cliente:</span> {isConsumidorFinal ? "Consumidor Final" : selectedCliente?.nombre}
              </div>
              <div>
                <span className="font-medium">CUIT:</span> {isConsumidorFinal ? "00-00000000-0" : selectedCliente?.cuit}
              </div>
              {!isConsumidorFinal && selectedCliente && (
                <>
                  <div>
                    <span className="font-medium">Email:</span> {selectedCliente.email}
                  </div>
                  <div>
                    <span className="font-medium">Teléfono:</span> {selectedCliente.telefono}
                  </div>
                  <div className="col-span-2">
                    <span className="font-medium">Dirección:</span> {selectedCliente.domicilio}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Agregar Productos</h3>
        <div className="flex gap-2">
          <div className="flex-1 relative" ref={productoDropdownRef}>
            <SearchInput
              value={productoQuery}
              onChange={(e) => {
                setProductoQuery(e.target.value);
                setShowProductoSuggestions(true);
                setProductoHighlight(0);
              }}
              onFocus={() => setShowProductoSuggestions(true)}
              onKeyDown={(e) =>
                handleProductSearchHotkey(
                  e,
                  filteredProductos.length,
                  (idx) => {
                    const p = filteredProductos[idx];
                    if (p) addProducto(p);
                  },
                  { index: productoHighlight, setIndex: setProductoHighlight },
                )
              }
              placeholder="Buscar producto..."
            />
            {showProductoSuggestions && filteredProductos.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-input rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredProductos.map((producto, i) => {
                  const precio = producto.precioFinal || producto.precio_final || producto.precioBase || 0;
                  const stock = producto.stockActual || producto.stock_actual || 0;
                  const highlighted =
                    i === clampProductSearchHighlight(productoHighlight, filteredProductos.length);

                  return (
                    <button
                      key={producto.codigo}
                      type="button"
                      onClick={() => addProducto(producto)}
                      className={cn(
                        "w-full px-4 py-2 text-left hover:bg-muted text-sm",
                        highlighted && "bg-muted",
                      )}
                    >
                      <div className="flex justify-between items-center gap-2">
                        <span className="truncate">
                          <span className="text-xs text-muted-foreground mr-2">{producto.codigo}</span>
                          {producto.nombre}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          ${precio.toLocaleString('es-AR')} | Stock: {stock}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <NumberField
            min="1"
            value={cantidad}
            onChange={(e) => setCantidad(parseInt(e.target.value) || 1)}
            className="w-24 px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            placeholder="Cant."
          />
          <Button
            type="button"
            variant="outline"
            title="Ítem personalizado"
            aria-label="Ítem personalizado"
            onClick={() => {
              if (ocultaPersonalizadosExistentes) {
                setShowConfigurador(true);
                return;
              }
              setPersonalizadoQuery("");
              setFoundPersonalizados([]);
              setShowPersonalizadoPicker(true);
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Pers.
          </Button>
          <Button
            type="button"
            variant="outline"
            title="Combinar productos"
            aria-label="Combinar productos"
            onClick={() => setShowComboDialog(true)}
          >
            <Link2 className="h-4 w-4 mr-1" />
            Combo
          </Button>
        </div>

        {productos.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-input">
            <table className="w-full min-w-[640px]">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-2 px-4 text-sm font-medium">Código</th>
                  <th className="text-left py-2 px-4 text-sm font-medium">Producto</th>
                  <th className="text-center py-2 px-4 text-sm font-medium">Cant.</th>
                  <th className="text-center py-2 px-4 text-sm font-medium">Desc.</th>
                  <th className="text-center py-2 px-4 text-sm font-medium">Desc.</th>
                  <th className="text-right py-2 px-4 text-sm font-medium">Precio Lista</th>
                  <th className="text-right py-2 px-4 text-sm font-medium">Total</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {groupComboVisualRows(productos).map((row) => {
                  const producto = row.legs[0];
                  const key = row.key;
                  const codigo = row.isCombo ? comboVisualCodigo(row.legs) : producto.codigo;
                  const nombre = row.isCombo ? comboVisualNombre(row.legs) : producto.nombre;
                  const precioLista = row.isCombo
                    ? comboVisualPrecioSum(row.legs)
                    : producto.precioBase;
                  const totalLinea = row.legs.reduce((s, p) => s + calcularTotalProducto(p), 0);
                  return (
                  <tr key={key} className="border-t border-input">
                    <td className="py-2 px-4 text-sm whitespace-pre-line">{codigo}</td>
                    <td className="py-2 px-4 text-sm">
                      <span className="block whitespace-normal break-words">
                        {nombre}
                        {!row.isCombo && producto.configSnapshot ? (
                          <span className="ml-2 text-xs text-muted-foreground">(personalizado)</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-center">
                      <NumberField
                        min="1"
                        value={producto.cantidad}
                        onChange={(e) => updateCantidad(key, parseInt(e.target.value) || 1)}
                        className="w-16 px-2 py-1 border border-input bg-background rounded text-sm text-center"
                      />
                    </td>
                    <td className="py-2 px-4 text-center">
                      <NumberField
                        min="0"
                        max="100"
                        value={producto.descuento1}
                        onChange={(e) => updateDescuento1(key, parseFloat(e.target.value) || 0)}
                        className="w-16 px-2 py-1 border border-input bg-background rounded text-sm text-center"
                      />
                    </td>
                    <td className="py-2 px-4 text-center">
                      <NumberField
                        min="0"
                        max="100"
                        value={producto.descuento2}
                        onChange={(e) => updateDescuento2(key, parseFloat(e.target.value) || 0)}
                        className="w-16 px-2 py-1 border border-input bg-background rounded text-sm text-center"
                      />
                    </td>
                    <td className="py-2 px-4 text-sm text-right">
                      ${precioLista.toLocaleString('es-AR')}
                    </td>
                    <td className="py-2 px-4 text-sm text-right font-medium">
                      ${totalLinea.toLocaleString('es-AR')}
                    </td>
                    <td className="py-2 px-4">
                      <button
                        onClick={() => removeProducto(key)}
                        className={deleteIconButtonClass}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
                <tr className="border-t-2 border-input bg-muted/50">
                  <td colSpan={6} className="py-3 px-4 text-right font-semibold">Total:</td>
                  <td className="py-3 px-4 text-right font-bold text-lg">
                    ${calcularTotal().toLocaleString('es-AR')}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          onClick={generarPresupuesto}
          disabled={
            pvBlocked
              ? false
              : ((!selectedCliente && !isConsumidorFinal) || !selectedPuntoVenta || productos.length === 0)
          }
          className="bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
        >
          Generar Presupuesto
        </Button>
      </div>

      <Dialog
        open={showPersonalizadoPicker}
        onOpenChange={(open) => {
          setShowPersonalizadoPicker(open);
          if (!open) {
            setPersonalizadoQuery("");
            setFoundPersonalizados([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Ítem personalizado</DialogTitle>
            <DialogDescription>
              Buscá un producto personalizado existente o creá uno nuevo con el
              configurador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Input
                value={personalizadoQuery}
                onChange={(e) => setPersonalizadoQuery(e.target.value)}
                placeholder="Buscar por código o nombre..."
                autoFocus
              />
              {searchingPersonalizados && (
                <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <div className="border rounded-lg max-h-56 overflow-y-auto">
              {foundPersonalizados.length === 0 && !searchingPersonalizados ? (
                <p className="px-3 py-6 text-sm text-muted-foreground text-center">
                  No hay productos personalizados
                  {personalizadoQuery.trim() ? " con ese criterio" : ""}.
                </p>
              ) : (
                foundPersonalizados.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-muted flex justify-between gap-2 border-b border-border last:border-b-0"
                    onClick={() => {
                      addProducto(p);
                      setShowPersonalizadoPicker(false);
                      setPersonalizadoQuery("");
                      setFoundPersonalizados([]);
                    }}
                  >
                    <span className="text-sm">
                      <span className="font-medium">{p.codigo}</span>
                      {" — "}
                      {p.nombre}
                    </span>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      $
                      {toNum(p.precio_final ?? p.precioFinal).toLocaleString("es-AR", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
          <DialogFooter className="sm:justify-end">
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPersonalizadoPicker(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowPersonalizadoPicker(false);
                  setShowConfigurador(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Crear nuevo
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfiguradorProductoDialog
        open={showConfigurador}
        onOpenChange={setShowConfigurador}
        persistMode="none"
        onSnapshot={addPersonalizadoSnapshot}
        canCreateProveedor={canCreateProveedorFromPersonalizado(user.modulePermissions, {
          isElevated: sessionIsElevated(user),
        })}
      />

      <ComboProductosDialog
        open={showComboDialog}
        onOpenChange={setShowComboDialog}
        initialQty={cantidad}
        onAdd={addComboLines}
      />

      <Dialog open={showNuevoCliente} onOpenChange={setShowNuevoCliente}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo cliente</DialogTitle>
            <DialogDescription>
              Alta con el mismo ABM de Clientes. El cliente quedará seleccionado en el presupuesto.
            </DialogDescription>
          </DialogHeader>
          {showNuevoCliente && (
            <ClientesABM
              createOnly
              onClienteSaved={(cliente) => {
                selectCliente(cliente);
                setShowNuevoCliente(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}